import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import './GisLightningAnalysis.css'

// Importing header
import TeamHeader from '../TeamHeader/TeamHeader'

// Filter
import FilterDropdown from "../TeamPortalUtil/FilterDropdown";
import DateFilter, { DateRange } from '../TeamPortalUtil/Date Filter/DateFilter';

// Chart Imports
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

// Loading import
import LoadingSpinner from '../../backend/components/LoadingSpinner';
import countiesGeoJson from './counties_southeast.json';

// Leaflet imports
import {
    MapContainer,
    TileLayer,
    ScaleControl,
    GeoJSON,
    useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import HeatmapOverlay from "leaflet-heatmap";
import type { Layer, LeafletMouseEvent, StyleFunction, GeoJSON as LeafletGeoJSON } from "leaflet";
import type { Feature, Geometry, FeatureCollection } from "geojson";

// Icons
import { BiPieChartAlt2 } from 'react-icons/bi'
import { BsList, BsInfoCircle } from 'react-icons/bs'
import { HiMiniGlobeAmericas } from "react-icons/hi2";

// Interfaces
type LightningStatus = 'Yes' | 'No' | 'Maybe'

type HrlBucket =
    | '0'
    | '10'
    | '20'
    | '30'
    | '40'
    | '50'
    | '60'
    | '70'
    | '80'
    | '90+'

interface NotionChildDatabase {
    id: string
    title: string
}

interface GisFire {
    id: string
    fid: number
    fire_name: string
    lightning_5day: LightningStatus | null
    hrl_rating: number | null
}

interface GisArchiveLightning {
    lightning_group_id: number;
    strikes: Record<string, number>;  // polygon string → strike count
    counties: Record<string, number>;
    date: string; // ISO string
    region: string;
}

interface CountyFeatureProps {
    NAME: string;
    STATEFP: string;
    STATE?: string;
    [key: string]: any;
}

/* -----------------------------------------------------------------------
   HeatMapLayer — renders a leaflet-heatmap overlay inside a MapContainer
----------------------------------------------------------------------- */
interface HeatPoint {
    lat: number;
    lng: number;
    count: number;
}

interface HeatMapLayerProps {
    data: HeatPoint[];
    onExtremaChange?: (min: number, max: number) => void;
}

const stateToAbbrev: Record<string, string> = {
    Florida: "FL",
    Alabama: "AL",
    Georgia: "GA",
    Texas: "TX",
    "South Carolina": "SC",
};

const HeatMapLayer: React.FC<HeatMapLayerProps> = ({ data, onExtremaChange }) => {
    const map = useMap();

    useEffect(() => {
        if (!map || data.length === 0) return;

        const cfg = {
            radius: 0.125,
            maxOpacity: 0.7,
            minOpacity: 0.1,
            scaleRadius: true,
            useLocalExtrema: true,
            latField: 'lat',
            lngField: 'lng',
            valueField: 'count',
            onExtremaChange: (d: { min: number; max: number }) => {
                onExtremaChange?.(d.min, d.max);
            },
        };

        const heat = new HeatmapOverlay(cfg);
        const maxVal = Math.max(...data.map(d => d.count));
        heat.setData({ max: maxVal, data });
        map.addLayer(heat);

        return () => {
            map.removeLayer(heat);
        };
    }, [map, data, onExtremaChange]);

    return null;
};

/* -----------------------------------------------------------------------
   Helper: parse the polygon-string key → midpoint { lat, lng }
   Each key looks like: "[[-80.3, 27.6], [-80.4, 27.6], [-80.4, 27.7], [-80.3, 27.7], [-80.3, 27.6]]"
   pairs are [lng, lat].
----------------------------------------------------------------------- */
function parseStrikesToHeatPoints(strikes: Record<string, number>): HeatPoint[] {
    const points: HeatPoint[] = [];
    for (const [key, count] of Object.entries(strikes)) {
        try {
            const coords: [number, number][] = JSON.parse(key);
            // coords[0] = SW corner [lng, lat], coords[2] = NE corner [lng, lat]
            const midLng = (coords[0][0] + coords[2][0]) / 2;
            const midLat = (coords[0][1] + coords[2][1]) / 2;
            points.push({ lat: midLat, lng: midLng, count });
        } catch {
            // skip malformed keys
        }
    }
    return points;
}


/* -----------------------------------------------------------------------
   Helper: color scale for county choropleth
   - 0 strikes (or no data)  -> grey
   - low strikes             -> green
   - mid strikes             -> yellow
   - high strikes            -> red
----------------------------------------------------------------------- */
function getCountyColor(count: number, max: number): string {
    if (max <= 0) return '#10B981';

    const ratio = Math.min(Math.max(count / max, 0), 1);

    // Three-stop gradient: green (#10B981) -> yellow (#FACC15) -> red (#EF4444)
    const stops: [number, [number, number, number]][] = [
        [0, [16, 185, 129]],   // green
        [0.5, [250, 204, 21]], // yellow
        [1, [239, 68, 68]],    // red
    ];

    let lower = stops[0];
    let upper = stops[stops.length - 1];
    for (let i = 0; i < stops.length - 1; i++) {
        if (ratio >= stops[i][0] && ratio <= stops[i + 1][0]) {
            lower = stops[i];
            upper = stops[i + 1];
            break;
        }
    }

    const span = upper[0] - lower[0] || 1;
    const t = (ratio - lower[0]) / span;

    const r = Math.round(lower[1][0] + (upper[1][0] - lower[1][0]) * t);
    const g = Math.round(lower[1][1] + (upper[1][1] - lower[1][1]) * t);
    const b = Math.round(lower[1][2] + (upper[1][2] - lower[1][2]) * t);

    return `rgb(${r}, ${g}, ${b})`;
}

/* -----------------------------------------------------------------------
   CountyChoroplethLayer — loads county GeoJSON, colors by strike count,
   and shows hover highlight + tooltip with county name + strike count.
----------------------------------------------------------------------- */
interface CountyChoroplethLayerProps {
    countyTotals: Record<string, number>;
    geojsonUrl?: string;
}

const CountyChoroplethLayer: React.FC<CountyChoroplethLayerProps> = ({
    countyTotals,
}) => {
    const geoJsonRef = useRef<LeafletGeoJSON | null>(null);

    const [geoData, setGeoData] = useState<FeatureCollection | null>(null);

    useEffect(() => {
        setGeoData(countiesGeoJson as FeatureCollection);
    }, []);

    // Build a normalized lookup map for county totals, and find max
    const { lookup, maxCount } = React.useMemo(() => {
        const lk: Record<string, number> = {};
        let max = 0;

        for (const [key, total] of Object.entries(countyTotals)) {
            lk[key] = total;
            if (total > max) max = total;
        }

        return { lookup: lk, maxCount: max };
    }, [countyTotals]);

    const getCount = useCallback((key: string): number => {
        return lookup[key] ?? 0;
    }, [lookup]);

    const filteredGeoData = useMemo<FeatureCollection | null>(() => {
        if (!geoData) return null;

        return {
            ...geoData,
            features: geoData.features.filter((feature) => {
                const name = feature.properties?.NAME ?? '';
                const state = feature.properties?.STATE ?? '';
                const stateAbbrev = stateToAbbrev[state] ?? state;

                const key = `${name} (${stateAbbrev})`;
                return getCount(key) > 0;
            }),
        };
    }, [geoData, getCount]);

    const styleFn: StyleFunction<CountyFeatureProps> = useCallback((feature) => {
        const name = feature?.properties?.NAME ?? '';
        const state = feature?.properties?.STATE ?? '';
        const stateAbbrev = stateToAbbrev[state] ?? state;

        const key = `${name} (${stateAbbrev})`;
        const count = getCount(key);
        return {
            fillColor: getCountyColor(count, maxCount),
            fillOpacity: 0.65,
            color: '#374151',
            weight: 1,
            interactive: count > 0,
        };
    }, [getCount, maxCount]);

    const onEachFeature = useCallback((feature: Feature<Geometry, CountyFeatureProps>, layer: Layer) => {
        const name = feature.properties?.NAME ?? '';
        const state = feature.properties?.STATE ?? '';
        const stateAbbrev = stateToAbbrev[state] ?? state;

        const key = `${name} (${stateAbbrev})`;
        const count = getCount(key);

        layer.bindTooltip(
            `<strong>${name} County (${stateAbbrev})</strong><br/>${count.toLocaleString()} strike${count === 1 ? '' : 's'}`,
            {
                sticky: false,
                direction: 'top',
            }
        );

        layer.on({
            mouseover: (e: LeafletMouseEvent) => {
                const target = e.target;

                target.setStyle({
                    weight: 3,
                    color: '#1F2937',
                    fillOpacity: 0.85,
                });

                target.bringToFront();
                target.openTooltip();
            },

            mouseout: (e: LeafletMouseEvent) => {
                const target = e.target;

                geoJsonRef.current?.resetStyle(target);
                target.closeTooltip();
            },
        });
    }, [getCount]);

    if (!filteredGeoData) return null;

    return (
        <GeoJSON
            key={`${Object.keys(countyTotals).length}-${maxCount}`}
            ref={geoJsonRef}
            data={filteredGeoData}
            style={styleFn as any}
            onEachFeature={onEachFeature as any}
        />
    );
};

/* -----------------------------------------------------------------------
   Inline gradient legend (heatmap)
----------------------------------------------------------------------- */
interface GradientLegendProps {
    min: number;
    max: number | null;
}

const GradientLegend: React.FC<GradientLegendProps> = ({ min, max }) => (
    <div style={{
        background: 'rgba(255,255,255,0.88)',
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 12,
        boxShadow: '0 1px 5px rgba(0,0,0,.2)',
    }}>
        <div style={{ marginBottom: 4, fontWeight: 600 }}>Strike Density</div>
        <div style={{
            width: 140,
            height: 12,
            borderRadius: 4,
            background: 'linear-gradient(to right, #0000ff, #00ffff, #00ff00, #ffff00, #ff0000)',
            marginBottom: 4,
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{min}</span>
            <span>{max ?? '…'}</span>
        </div>
    </div>
);

/* -----------------------------------------------------------------------
   Inline legend for the county choropleth (green -> yellow -> red, grey = 0)
----------------------------------------------------------------------- */
interface CountyLegendProps {
    max: number;
}

const CountyLegend: React.FC<CountyLegendProps> = ({ max }) => (
    <div style={{
        background: 'rgba(255,255,255,0.88)',
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 12,
        boxShadow: '0 1px 5px rgba(0,0,0,.2)',
    }}>
        <div style={{ marginBottom: 4, fontWeight: 600 }}>Strikes by County</div>
        <div style={{
            width: 140,
            height: 12,
            borderRadius: 4,
            background: 'linear-gradient(to right, #10B981, #FACC15, #EF4444)',
            marginBottom: 4,
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>1</span>
            <span>{max > 0 ? max.toLocaleString() : '…'}</span>
        </div>
    </div>
);

/* ======================================================================
   Main component
====================================================================== */
const GisLightningAnalysis = () => {

    const [availableRegions, setAvailableRegions] = useState<string[]>([]);
    const [regionDatabases, setRegionDatabases] = useState<NotionChildDatabase[]>([]);
    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1600);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 1600);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /* ------------------------------------------------------------------
       Overview (GIS fire database)
    ------------------------------------------------------------------ */
    const [gisData, setGisData] = useState<GisFire[]>([])
    const [loadingOverview, setLoadingOverview] = useState(true)
    const [overviewViewMode, setOverviewViewMode] = useState<'chart' | 'list'>('chart')

    useEffect(() => {
        const fetchChildDatabases = async () => {
            try {
                const res = await fetch(
                    '/api/notion/page-blocks/1ff3634fc8d980c1913fec9c91defe86/',
                    { credentials: "same-origin" }
                )
                const data = await res.json()
                const childDbs: NotionChildDatabase[] = data.blocks
                    .filter((block: any) => block.type === 'child_database')
                    .map((block: any) => ({
                        id: block.id.replace(/-/g, ''),
                        title: block.child_database.title,
                    }))
                setRegionDatabases(childDbs)
                setAvailableRegions(childDbs.map(db => db.title))
                if (childDbs.length > 0) {
                    const defaultIndex = childDbs.length > 3 ? 3 : 0
                    setSelectedRegion(childDbs[defaultIndex].title)
                }
            } catch (err) {
                console.error('Failed fetching region databases:', err)
            }
        }
        fetchChildDatabases()
    }, [])

    useEffect(() => {
        if (!selectedRegion) return
        const fetchRegionData = async () => {
            try {
                setLoadingOverview(true)
                const selectedDb = regionDatabases.find(db => db.title === selectedRegion)
                if (!selectedDb) return
                const res = await fetch(
                    `/api/notion/gis/fire-database/${selectedDb.id}`,
                    { credentials: "same-origin" }
                )
                const data: GisFire[] = await res.json()
                setGisData(data)
            } catch (err) {
                console.error('Failed fetching GIS data:', err)
            } finally {
                setLoadingOverview(false)
            }
        }
        fetchRegionData()
    }, [selectedRegion, regionDatabases])

    // Lightning ignitions
    const lightningCounts: Record<LightningStatus, number> = { Yes: 0, No: 0, Maybe: 0 }
    const lightningColorMap: Record<LightningStatus, string> = {
        Yes: '#29ABE2', No: '#0A6AA6', Maybe: '#1976D2',
    }
    gisData.forEach(row => { if (row.lightning_5day) lightningCounts[row.lightning_5day]++ })
    const lightningLabels = Object.keys(lightningCounts)
    const lightningSeries = Object.values(lightningCounts)
    const lightningTotal = lightningSeries.reduce((a, b) => a + b, 0)
    const donutTotalFontSize = isSmallScreen ? '12px' : '20px'
    const valueSpacing = isSmallScreen ? -20 : -25
    const nameSpacing = isSmallScreen ? 18 : 20

    // HRL buckets
    const hrlBuckets: Record<HrlBucket, number> = {
        '0': 0, '10': 0, '20': 0, '30': 0,
        '40': 0, '50': 0, '60': 0, '70': 0, '80': 0, '90+': 0,
    }

    const HRL_COLOR_SCALE: Record<HrlBucket, string> = {
        '0': '#9e0142', '10': '#d53e4f', '20': '#f46d43', '30': '#fdae61',
        '40': '#fee08b', '50': '#e6f598', '60': '#abdda4', '70': '#66c2a5',
        '80': '#3288bd', '90+': '#5e4fa2',
    }

    const getHrlColor = (bucket: HrlBucket) => HRL_COLOR_SCALE[bucket]

    const HRL_LABEL_MAP: Record<HrlBucket, string> = {
        '0': '0-10',
        '10': '10-20',
        '20': '20-30',
        '30': '30-40',
        '40': '40-50',
        '50': '50-60',
        '60': '60-70',
        '70': '70-80',
        '80': '80-90',
        '90+': '90-100',
    };

    const getHrlLabel = (bucket: HrlBucket) => HRL_LABEL_MAP[bucket];

    gisData.forEach(row => {
        const value = row.hrl_rating

        // 🚫 Completely ignore missing values
        if (value === null || value === undefined) return

        const bucket = Math.floor(value / 10) * 10

        if (bucket >= 90) hrlBuckets['90+']++
        else hrlBuckets[bucket.toString() as HrlBucket]++
    })

    const hrlLabels = Object.keys(hrlBuckets)
    const hrlSeries = Object.values(hrlBuckets)
    const hrlTotal = hrlSeries.reduce((a, b) => a + b, 0)

    const lightningChartOptions: ApexOptions = {
        chart: { type: 'donut' },
        labels: lightningLabels,
        colors: lightningLabels.map(l => lightningColorMap[l as LightningStatus]),
        legend: { position: 'bottom', fontSize: '12px', horizontalAlign: 'center', offsetY: 0, markers: { size: 5 } },
        plotOptions: {
            pie: {
                donut: {
                    size: '80%',
                    labels: {
                        show: true,
                        total: { show: true, label: 'Total', fontSize: donutTotalFontSize, fontWeight: 600, color: '#373d3f', formatter: () => lightningTotal.toString() },
                        name: { show: true, offsetY: nameSpacing },
                        value: { show: true, fontSize: donutTotalFontSize, color: '#373d3f', offsetY: valueSpacing },
                    },
                },
            },
        },
        tooltip: { y: { formatter: (v: number) => `${v} (${lightningTotal ? ((v / lightningTotal) * 100).toFixed(1) : '0'}%)` } },
        dataLabels: { enabled: false },
    }

    const hrlChartOptions: ApexOptions = {
        chart: { type: 'donut' },
        labels: hrlLabels.map(l => getHrlLabel(l as HrlBucket)),   // ← changed
        colors: hrlLabels.map(l => getHrlColor(l as HrlBucket)),
        legend: { position: 'bottom', fontSize: '12px', horizontalAlign: 'center', offsetY: 0, markers: { size: 5 } },
        plotOptions: {
            pie: {
                donut: {
                    size: '80%',
                    labels: {
                        show: true,
                        total: { show: true, label: 'Total', fontSize: donutTotalFontSize, fontWeight: 600, color: '#373d3f', formatter: () => hrlTotal.toString() },
                        name: { show: true, offsetY: nameSpacing },
                        value: { show: true, fontSize: donutTotalFontSize, color: '#373d3f', offsetY: valueSpacing },
                    },
                },
            },
        },
        tooltip: { y: { formatter: (v: number) => `${v} (${hrlTotal ? ((v / hrlTotal) * 100).toFixed(1) : '0'}%)` } },
        dataLabels: { enabled: false },
    }

    /* ------------------------------------------------------------------
       Archive Lightning Data
    ------------------------------------------------------------------ */
    const [archiveLightningData, setArchiveLightningData] = useState<GisArchiveLightning[]>([]);
    const [loadingArchive, setLoadingArchive] = useState(true);

    useEffect(() => {
        const fetchArchiveData = async () => {
            try {
                setLoadingArchive(true);
                const res = await fetch(
                    '/apidet/gis_lightning_archive/',
                    { credentials: "same-origin" }
                );
                const data: GisArchiveLightning[] = await res.json();
                setArchiveLightningData(data);
            } catch (err) {
                console.error('Failed fetching archive lightning data:', err);
            } finally {
                setLoadingArchive(false);
            }
        };
        fetchArchiveData();
    }, []);

    const getTotalStrikes = (strikes: Record<string, number>): number =>
        Object.values(strikes).reduce((a, b) => a + b, 0);

    const parseLocalDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return { year, month, day };
    };

    /** Correctly checks whether a (month, day) falls within [startDate, endDate],
        ignoring year (year is checked separately). Handles single-day ranges too. */
    const isDateInRange = (
        month: number,
        day: number,
        startDate: Date | null,
        endDate: Date | null
    ): boolean => {
        if (!startDate || !endDate) return true;
        const sm = startDate.getMonth() + 1, sd = startDate.getDate();
        const em = endDate.getMonth() + 1, ed = endDate.getDate();
        return (month > sm || (month === sm && day >= sd)) &&
            (month < em || (month === em && day <= ed));
    };

    const archiveFlatData = archiveLightningData.map(item => ({
        date: item.date.split('T')[0],
        total: getTotalStrikes(item.strikes),
        region: item.region,
    }));

    /* ------------------------------------------------------------------
       Shared constants derived from data
    ------------------------------------------------------------------ */
    const AVAILABLE_YEARS = Array.from(
        new Set(archiveFlatData.map(item => parseLocalDate(item.date).year))
    ).sort((a, b) => b - a);
    const yearsForFilter = AVAILABLE_YEARS.length > 0 ? AVAILABLE_YEARS : [new Date().getFullYear()];


    const archiveRegions = Array.from(new Set(archiveLightningData.map(d => d.region))).sort();

    /* ------------------------------------------------------------------
       Map filter state
    ------------------------------------------------------------------ */
    const [mapSelectedRegion, setMapSelectedRegion] = useState<string>('All');
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const [mapSelectedYears, setMapSelectedYears] = useState<number[]>([yesterday.getFullYear()]);
    const [mapStartDate, setMapStartDate] = useState<Date | null>(yesterday);
    const [mapEndDate, setMapEndDate] = useState<Date | null>(yesterday);
    const [heatmapMax, setHeatmapMax] = useState<number | null>(null);

    // Merge all matching archive groups into one heatmap point set
    const heatmapData: HeatPoint[] = React.useMemo(() => {
        const filtered = archiveLightningData.filter(item => {
            const { year, month, day } = parseLocalDate(item.date.split('T')[0]);
            const regionMatch = mapSelectedRegion === 'All' || item.region === mapSelectedRegion;
            const yearMatch = mapSelectedYears.length === 0 || mapSelectedYears.includes(year);
            const dateMatch = isDateInRange(month, day, mapStartDate, mapEndDate);
            return regionMatch && yearMatch && dateMatch;
        });

        const merged: Record<string, number> = {};
        for (const item of filtered) {
            for (const [key, count] of Object.entries(item.strikes)) {
                merged[key] = (merged[key] ?? 0) + count;
            }
        }

        return parseStrikesToHeatPoints(merged);
    }, [archiveLightningData, mapSelectedRegion, mapSelectedYears, mapStartDate, mapEndDate]);

    const handleHeatmapExtrema = useCallback((_min: number, max: number) => {
        setHeatmapMax(max);
    }, []);

    /* ------------------------------------------------------------------
        Season Comparison filter state
    ------------------------------------------------------------------ */
    const [seasonSelectedRegion, setSeasonSelectedRegion] = useState<string>('All');

    const [dateRanges, setDateRanges] = useState<DateRange[]>([{
        id: "initial",
        label: "Season Range",
        startDate: yesterday,
        endDate: yesterday,
        selectedYear: yesterday.getFullYear(),
    }]);

    // ── helpers ──────────────────────────────────────────────────────────

    /** Returns true if a range starts and ends within the same calendar month */
    const isSameMonth = (r: DateRange): boolean => {
        if (!r.startDate || !r.endDate) return false;
        return r.startDate.getMonth() === r.endDate.getMonth();
    };

    /** Filter archiveFlatData to rows that fall within a range's year + date window */

    const seasonFilteredFlatData = React.useMemo(() =>
        archiveFlatData.filter(item =>
            seasonSelectedRegion === 'All' || item.region === seasonSelectedRegion
        ),
        [archiveFlatData, seasonSelectedRegion]
    );

    const getDataForRange = (r: DateRange) => {
        return seasonFilteredFlatData.filter(item => {
            const { year, month, day } = parseLocalDate(item.date);
            if (year !== r.selectedYear) return false;
            if (!r.startDate || !r.endDate) return true;
            const sm = r.startDate.getMonth() + 1, sd = r.startDate.getDate();
            const em = r.endDate.getMonth() + 1, ed = r.endDate.getDate();
            return (month > sm || (month === sm && day >= sd)) &&
                (month < em || (month === em && day <= ed));
        });
    };

    /** Sum total strikes across all rows for a range */
    const sumRange = (r: DateRange): number =>
        getDataForRange(r).reduce((acc, item) => acc + item.total, 0);

    // ── determine chart mode ─────────────────────────────────────────────
    type SeasonMode =
        | 'multi-range-same-month'   // 2+ ranges, all within same month  → area, x = days
        | 'multi-range-diff-months'  // 2+ ranges, different months/years → bar,  x = range labels
        | 'single-day'               // 1 range, start === end             → bar,  x = date
        | 'single-same-month'        // 1 range, within one month          → area, x = days
        | 'single-multi-month';      // 1 range spanning months / no dates → area, x = months

    const seasonMode: SeasonMode = React.useMemo((): SeasonMode => {
        const active = dateRanges.filter(r => r.startDate && r.endDate);

        if (active.length > 1) {

            // 1️⃣ Each range must individually stay inside a month
            const allIndividuallySingleMonth = active.every(isSameMonth);
            if (!allIndividuallySingleMonth) {
                return 'multi-range-diff-months';
            }

            // 2️⃣ All ranges must share the SAME MONTH NUMBER
            const firstMonth = active[0].startDate!.getMonth();

            const allSameMonth = active.every(r =>
                r.startDate!.getMonth() === firstMonth
            );

            return allSameMonth
                ? 'multi-range-same-month'
                : 'multi-range-diff-months';
        }

        const r = active[0] ?? dateRanges[0];

        if (!r?.startDate || !r?.endDate)
            return 'single-multi-month';

        if (r.startDate.toDateString() === r.endDate.toDateString())
            return 'single-day';

        return isSameMonth(r)
            ? 'single-same-month'
            : 'single-multi-month';

    }, [dateRanges]);

    // ── ApexCharts option builders ───────────────────────────────────────
    const baseAreaOptions = (categories: (string | number)[], xTitle: string): ApexOptions => ({
        chart: { type: 'area', toolbar: { show: false } },
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.05, stops: [0, 90, 100] } },
        dataLabels: { enabled: false },
        xaxis: { categories, title: { text: xTitle } },
        yaxis: { title: { text: 'Total Lightning Strikes' } },
        legend: { position: 'top', horizontalAlign: 'center' },
        tooltip: { y: { formatter: (v: number) => `${v} strikes` } },
    });

    const baseBarOptions = (categories: (string | number)[], xTitle: string): ApexOptions => ({
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 2, columnWidth: '30%' } },
        dataLabels: { enabled: false },
        xaxis: { categories, title: { text: xTitle } },
        yaxis: { title: { text: 'Total Lightning Strikes' } },
        legend: { position: 'top', horizontalAlign: 'center' },
        tooltip: { y: { formatter: (v: number) => `${v} strikes` } },
    });

    // ── build chart data per mode ────────────────────────────────────────
    const seasonChartProps = React.useMemo(() => {
        const activeRanges = dateRanges.filter(r => r.startDate && r.endDate);
        const singleRange = dateRanges[0];

        // ----------------------------------------------------------------
        // MULTI-RANGE, SAME MONTH → area, x = days, one series per range
        // ----------------------------------------------------------------
        if (seasonMode === 'multi-range-same-month') {
            const allDays = Array.from(new Set(
                activeRanges.flatMap(r =>
                    getDataForRange(r).map(item => parseLocalDate(item.date).day)
                )
            )).sort((a, b) => a - b);

            const series = activeRanges.map((r, i) => ({
                name: r.label || `Range ${i + 1}`,
                data: allDays.map(day => {
                    const found = getDataForRange(r).find(
                        item => parseLocalDate(item.date).day === day
                    );
                    return found ? found.total : 0;
                }),
            }));

            return {
                options: baseAreaOptions(allDays, 'Day of Month'),
                series,
                type: 'area' as const,
                needsScroll: allDays.length > 12,
                scrollWidth: `${allDays.length * 65}px`,
            };
        }

        // ----------------------------------------------------------------
        // MULTI-RANGE, DIFF MONTHS → bar, x = range labels, 1 bar per range
        // ----------------------------------------------------------------
        if (seasonMode === 'multi-range-diff-months') {
            const categories = activeRanges.map((r, i) => r.label || `Range ${i + 1}`);
            return {
                options: baseBarOptions(categories, 'Date Range'),
                series: [{ name: 'Total Strikes', data: activeRanges.map(r => sumRange(r)) }],
                type: 'bar' as const,
                needsScroll: categories.length > 12,
                scrollWidth: `${Math.max(categories.length * 80, 400)}px`,
            };
        }

        // ----------------------------------------------------------------
        // SINGLE DAY → bar, x = formatted date
        // ----------------------------------------------------------------
        if (seasonMode === 'single-day') {
            const dateLabel = singleRange?.startDate
                ? singleRange.startDate.toLocaleDateString('en-US', {
                    month: '2-digit', day: '2-digit', year: 'numeric',
                })
                : 'Selected Day';

            return {
                options: baseBarOptions([dateLabel], 'Date'),
                series: [{ name: 'Total Strikes', data: [sumRange(singleRange)] }],
                type: 'bar' as const,
                needsScroll: false,
                scrollWidth: '100%',
            };
        }

        // ----------------------------------------------------------------
        // SINGLE SAME MONTH → area, x = days
        // ----------------------------------------------------------------
        if (seasonMode === 'single-same-month') {
            const rangeData = getDataForRange(singleRange);
            const days = Array.from(
                new Set(rangeData.map(item => parseLocalDate(item.date).day))
            ).sort((a, b) => a - b);

            return {
                options: baseAreaOptions(days, 'Day of Month'),
                series: [{
                    name: singleRange.label || singleRange.selectedYear.toString(),
                    data: days.map(day => {
                        const found = rangeData.find(item => parseLocalDate(item.date).day === day);
                        return found ? found.total : 0;
                    }),
                }],
                type: 'area' as const,
                needsScroll: days.length > 12,
                scrollWidth: `${days.length * 65}px`,
            };
        }

        // ----------------------------------------------------------------
        // SINGLE MULTI-MONTH (or no dates) → area, x = months
        // ----------------------------------------------------------------
        const year = singleRange?.selectedYear ?? new Date().getFullYear();

        if (!singleRange?.startDate || !singleRange?.endDate) {
            // No filter: show every month with data for this year
            const monthTotals: Record<number, number> = {};
            seasonFilteredFlatData
                .filter(item => parseLocalDate(item.date).year === year)
                .forEach(item => {
                    const { month } = parseLocalDate(item.date);
                    monthTotals[month] = (monthTotals[month] ?? 0) + item.total;
                });
            const months = Object.keys(monthTotals).map(Number).sort((a, b) => a - b);
            const categories = months.map(m => new Date(0, m - 1).toLocaleString('en-US', { month: 'short' }));
            return {
                options: baseAreaOptions(categories, 'Month'),
                series: [{ name: year.toString(), data: months.map(m => monthTotals[m]) }],
                type: 'area' as const,
                needsScroll: false,
                scrollWidth: '100%',
            };
        }

        // Range spans multiple months
        const months: number[] = [];
        let cur = new Date(singleRange.startDate.getFullYear(), singleRange.startDate.getMonth(), 1);
        const endCap = new Date(singleRange.endDate.getFullYear(), singleRange.endDate.getMonth(), 1);
        while (cur <= endCap) {
            months.push(cur.getMonth() + 1);
            cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
        }
        const categories = months.map(m => new Date(0, m - 1).toLocaleString('en-US', { month: 'short' }));
        return {
            options: baseAreaOptions(categories, 'Month'),
            series: [{
                name: singleRange.label || year.toString(),
                data: months.map(month =>
                    seasonFilteredFlatData
                        .filter(item => {
                            const p = parseLocalDate(item.date);
                            return p.year === year && p.month === month;
                        })
                        .reduce((acc, item) => acc + item.total, 0)
                ),
            }],
            type: 'area' as const,
            needsScroll: categories.length > 12,
            scrollWidth: `${categories.length * 65}px`,
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seasonMode, dateRanges, seasonFilteredFlatData]);

    /* ------------------------------------------------------------------
       Regions Aggregation
    ------------------------------------------------------------------ */

    const [lightningRegionsViewMode, setLightningRegionsViewMode] = useState<'chart' | 'list'>('chart')
    const [regionSelectedYears, setRegionSelectedYears] = useState<number[]>([yesterday.getFullYear()]);
    const [regionStartDate, setRegionStartDate] = useState<Date | null>(yesterday);
    const [regionEndDate, setRegionEndDate] = useState<Date | null>(yesterday);
    const [regionSelectedRegion, setRegionSelectedRegion] = useState<string>('All');

    const regionFilteredData = archiveFlatData.filter(item => {
        const { year, month, day } = parseLocalDate(item.date);
        return (
            regionSelectedYears.includes(year) &&
            (regionSelectedRegion === 'All' || item.region === regionSelectedRegion) &&
            isDateInRange(month, day, regionStartDate, regionEndDate)
        );
    });

    const regionTotals: Record<string, number> = {};
    regionFilteredData.forEach(item => {
        regionTotals[item.region] = (regionTotals[item.region] ?? 0) + item.total;
    });
    const sortedRegions = Object.entries(regionTotals).sort((a, b) => b[1] - a[1]);
    const regionSeries = sortedRegions.map(([, t]) => t);
    const regionTotalSum = regionSeries.reduce((a, b) => a + b, 0);



    /* ------------------------------------------------------------------
       County Aggregation
    ------------------------------------------------------------------ */
    const countyFilteredArchive = archiveLightningData.filter(item => {
        const { year, month, day } = parseLocalDate(item.date.split('T')[0]);
        return (
            regionSelectedYears.includes(year) &&
            (regionSelectedRegion === 'All' || item.region === regionSelectedRegion) &&
            isDateInRange(month, day, regionStartDate, regionEndDate)
        );
    });

    // Merge all county strike maps
    const countyTotals: Record<string, number> = {};

    countyFilteredArchive.forEach(item => {
        Object.entries(item.counties || {}).forEach(([county, count]) => {
            const stateAbbrev = stateToAbbrev[item.region] ?? item.region;

            const key = `${county} (${stateAbbrev})`;

            countyTotals[key] = (countyTotals[key] ?? 0) + count;
        });
    });

    // Sort descending
    const sortedCounties = Object.entries(countyTotals).sort((a, b) => b[1] - a[1]);

    // Take only top 5 counties (used for the list view)
    const topCounties = sortedCounties.slice(0, 5);
    const othersTotal = sortedCounties.slice(5).reduce((sum, [, v]) => sum + v, 0);

    const countyLabels = topCounties.map(([c]) => c);
    const countySeries = topCounties.map(([, v]) => v);

    if (othersTotal > 0) {
        countyLabels.push("Others");
        countySeries.push(othersTotal);
    }

    const countyTotalSum = countySeries.reduce((a, b) => a + b, 0);

    // Max strike count across ALL counties (used for the choropleth color scale,
    // not just the top 5)
    const countyMaxCount = sortedCounties.length > 0 ? sortedCounties[0][1] : 0;

    /* ====================================================================
       RENDER
    ==================================================================== */
    return (
        <div className="customer-directory-body toa-body">
            <TeamHeader title="GIS" />

            <div className="customer-title-and-expand-buttons">
                <div className="gis-title-with-info">
                    <h1 className="cutomer-title">Lightning Analysis</h1>
                    <span className="gis-info-icon-wrapper">
                        <BsInfoCircle className="gis-info-icon" />
                        <div className="gis-info-banner">
                            This dashboard defaults to showing yesterday&apos;s data and updates daily.
                            To filter by a single day, select that day twice in the date picker (start and end date the same).
                        </div>
                    </span>
                </div>
            </div>

            <div className='toa-dash-container'>

                {/* ── Map Block ── */}
                <div className="toa-dash-block">

                    {loadingArchive ? (
                        <div className="toa-map-skeleton">
                            <LoadingSpinner />
                        </div>
                    ) : (
                        <div className="toa-map-wrapper" style={{ position: 'relative' }}>

                            {/* Filter buttons */}
                            <div className='gis-in-map-buttons'>
                                <FilterDropdown
                                    sideClass="detector-side-drop"
                                    initialSelected={{
                                        regions: [mapSelectedRegion === 'All' ? 'All Regions' : mapSelectedRegion],
                                    }}
                                    sections={[
                                        {
                                            key: 'regions',
                                            title: 'Region',
                                            options: ['All Regions', ...archiveRegions],
                                            type: 'radio',
                                        },
                                    ]}
                                    onChange={(filters) => {
                                        if (filters.regions?.length) {
                                            setMapSelectedRegion(
                                                filters.regions[0] === 'All Regions'
                                                    ? 'All'
                                                    : filters.regions[0]
                                            );
                                        }
                                    }}
                                />
                                <DateFilter
                                    dropdownClassName='map-calendar-dropdown'
                                    availableYears={yearsForFilter}
                                    maxCalendars={1}
                                    initialRanges={[{
                                        id: "map-range",
                                        label: "Date Range",
                                        startDate: yesterday,
                                        endDate: yesterday,
                                        selectedYear: yesterday.getFullYear(),
                                    }]}
                                    onChange={(ranges: DateRange[]) => {
                                        const r = ranges[0];
                                        if (!r) return;

                                        setMapSelectedYears([r.selectedYear]);
                                        setMapStartDate(r.startDate ?? null);
                                        setMapEndDate(r.endDate ?? null);
                                    }}
                                />
                            </div>

                            <MapContainer
                                center={[29.0, -82.5]}
                                zoom={5}
                                minZoom={3}
                                style={{ width: '100%', height: '100%' }}
                            >
                                <TileLayer
                                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                />
                                <ScaleControl position="bottomleft" metric={true} imperial={false} maxWidth={200} />
                                {heatmapData.length > 0 && (
                                    <HeatMapLayer data={heatmapData} onExtremaChange={handleHeatmapExtrema} />
                                )}
                            </MapContainer>

                            {/* Gradient legend overlay */}
                            <div style={{ position: 'absolute', bottom: 25, right: 5, zIndex: 999 }}>
                                <GradientLegend min={1} max={heatmapMax} />
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Regions Chart/List Block ── */}
                <div className="toa-dash-block marketing-chart-block" style={{ position: 'relative' }}>

                    {/* Dynamic class added here based on view mode */}
                    <div className={`request-title-and-times-in-gis toa-location ${lightningRegionsViewMode === 'chart' ? 'overlay-header' : ''}`}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>

                            <div className='gis-chart-and-filter'>
                                <div className="marketing-chart-table-toggle gis-filt-ext">
                                    <HiMiniGlobeAmericas
                                        className={lightningRegionsViewMode === 'chart' ? 'active' : ''}
                                        onClick={() => setLightningRegionsViewMode('chart')}
                                    />
                                    <BsList
                                        className={lightningRegionsViewMode === 'list' ? 'active' : ''}
                                        onClick={() => setLightningRegionsViewMode('list')}
                                    />
                                </div>
                            </div>


                            <FilterDropdown
                                dropdownClassName='left-calendar-dropdown'
                                sideClass="detector-side-drop"
                                initialSelected={{
                                    regions: [regionSelectedRegion === 'All' ? 'All Regions' : regionSelectedRegion],
                                }}
                                sections={[
                                    {
                                        key: 'regions',
                                        title: 'Region',
                                        options: ['All Regions', ...archiveRegions],
                                        type: 'radio',
                                    },
                                ]}
                                onChange={(filters) => {
                                    if (filters.regions?.length) {
                                        setRegionSelectedRegion(
                                            filters.regions[0] === 'All Regions' ? 'All' : filters.regions[0]
                                        );
                                    }
                                }}
                            />
                            <DateFilter
                                dropdownClassName='left-calendar-dropdown'
                                availableYears={yearsForFilter}
                                maxCalendars={1}
                                initialRanges={[{
                                    id: "regions-range",
                                    label: "Date Range",
                                    startDate: yesterday,
                                    endDate: yesterday,
                                    selectedYear: yesterday.getFullYear(),
                                }]}
                                onChange={(ranges: DateRange[]) => {
                                    const r = ranges[0];
                                    if (!r) return;
                                    setRegionSelectedYears([r.selectedYear]);
                                    setRegionStartDate(r.startDate ?? null);
                                    setRegionEndDate(r.endDate ?? null);
                                }}
                            />
                        </div>
                    </div>

                    {loadingArchive ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <LoadingSpinner />
                        </div>
                    ) : lightningRegionsViewMode === 'chart' ? (
                        <div className='toa-charts'>

                            {/* ── Strikes by County: now a choropleth map ── */}
                            <div className='toa-chart'>
                                <div className="toa-map-wrapper" style={{ position: 'relative' }}>

                                    {countyTotalSum === 0 ? (
                                        <div className="no-data-message">
                                            No data for the selected time period.
                                        </div>
                                    ) : (
                                        <>
                                            <MapContainer
                                                center={[29.0, -82.5]}
                                                zoom={5}
                                                minZoom={3}
                                                style={{ width: '100%', height: '100%' }}
                                                scrollWheelZoom={true}
                                            >
                                                <TileLayer
                                                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                                />

                                                <CountyChoroplethLayer countyTotals={countyTotals} />
                                            </MapContainer>

                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    bottom: 25,
                                                    right: 5,
                                                    zIndex: 999,
                                                }}
                                            >
                                                <CountyLegend max={countyMaxCount} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className='marketing-lists'>
                            {/* ── Strikes by Region ── */}
                            <div className='marketing-list'>
                                <h1 className='marketing-list-title'>Strikes by Region</h1>
                                <ul className='marketing-stats-list'>
                                    {sortedRegions.map(([region, total]) => {
                                        const percent = regionTotalSum
                                            ? ((total / regionTotalSum) * 100).toFixed(1)
                                            : '0';

                                        return (
                                            <li key={region}>
                                                <span className='marketing-label'>{region}</span>
                                                <span className='marketing-value'>
                                                    {total.toLocaleString()} ({percent}%)
                                                </span>
                                            </li>
                                        );
                                    })}
                                    <li className='marketing-total'>
                                        <span className='marketing-label'>Total</span>
                                        <span className='marketing-value'>
                                            {regionTotalSum.toLocaleString()}
                                        </span>
                                    </li>
                                </ul>
                            </div>

                            {/* ── Strikes by County ── */}
                            <div className='marketing-list'>
                                <h1 className='marketing-list-title'>Strikes by County</h1>
                                <ul className='marketing-stats-list'>
                                    {sortedCounties.map(([county, total]) => {
                                        const percent = countyTotalSum
                                            ? ((total / countyTotalSum) * 100).toFixed(1)
                                            : '0';

                                        return (
                                            <li key={county}>
                                                <span className='marketing-label'>{county}</span>
                                                <span className='marketing-value'>
                                                    {total.toLocaleString()} ({percent}%)
                                                </span>
                                            </li>
                                        );
                                    })}
                                    <li className='marketing-total'>
                                        <span className='marketing-label'>Total</span>
                                        <span className='marketing-value'>
                                            {countyTotalSum.toLocaleString()}
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>



                {/* ── Season Comparison Block ── */}
                <div className="toa-dash-block">
                    <div className="request-title-and-times toa-location">
                        <h1 className="toa-dash-block-performance-title">Season Comparison</h1>

                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <FilterDropdown
                                dropdownClassName='top-calendar-dropdown'
                                sideClass="detector-side-drop"
                                initialSelected={{
                                    regions: [seasonSelectedRegion === 'All' ? 'All Regions' : seasonSelectedRegion],
                                }}
                                sections={[
                                    {
                                        key: 'regions',
                                        title: 'Region',
                                        options: ['All Regions', ...archiveRegions],
                                        type: 'radio',
                                    },
                                ]}
                                onChange={(filters) => {
                                    if (filters.regions?.length) {
                                        setSeasonSelectedRegion(
                                            filters.regions[0] === 'All Regions' ? 'All' : filters.regions[0]
                                        );
                                    }
                                }}
                            />
                            <DateFilter
                                dropdownClassName='top-calendar-dropdown'
                                availableYears={yearsForFilter}
                                initialRanges={[{
                                    id: "initial",
                                    label: "Season Range",
                                    startDate: yesterday,
                                    endDate: yesterday,
                                    selectedYear: yesterday.getFullYear(),
                                }]}
                                onChange={(ranges: DateRange[]) => {
                                    setDateRanges(ranges);
                                }}
                            />
                        </div>
                    </div>

                    <div className="toa-chart efficiency-legend-fix">
                        {loadingArchive ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <LoadingSpinner />
                            </div>
                        ) : seasonChartProps.series.some(s =>
                            s.data.some(value => value > 0)
                        ) ? (
                            <div className="efficiency-chart-scroll-wrapper gis-light-ext">
                                <div
                                    className="efficiency-chart-inner"
                                    style={{ width: seasonChartProps.needsScroll ? seasonChartProps.scrollWidth : '100%' }}
                                >
                                    <ReactApexChart
                                        key={`${seasonMode}-${seasonChartProps.series.map(s => s.name).join('-')}`}
                                        className="season-area-chart"
                                        options={seasonChartProps.options}
                                        series={seasonChartProps.series}
                                        type={seasonChartProps.type}
                                        height="100%"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="no-data-message">No data for the selected time period.</div>
                        )}
                    </div>
                </div>

                {/* ── Overview Chart/List Block ── */}
                <div className="toa-dash-block">
                    <div className="request-title-and-times toa-location">
                        <div className='gis-chart-and-filter'>
                            <div className="gis-title-with-info">
                                <h1 className="toa-dash-block-performance-title">GIS Overview</h1>
                                <span className="gis-info-icon-wrapper">
                                    <BsInfoCircle className="gis-info-icon" />
                                    <div className="gis-info-banner">
                                        Fires associated with HRL within 5 days and 2 km.
                                        <br />
                                        <br />
                                        Ignitions Legend:
                                        <br />
                                        * Yes: The fire has an HRL lightning associated
                                        <br />
                                        * No: There is no HRL lightning associated.
                                        <br />
                                        <br />
                                        HRL Legend:
                                        <br />
                                        Risk of each lightning igniting a fire. Values closer to 0 indicate higher risk and closer to 100 indicate lower risk.
                                    </div>
                                </span>
                            </div>


                            <div className="marketing-chart-table-toggle">
                                <BiPieChartAlt2
                                    className={overviewViewMode === 'chart' ? 'active' : ''}
                                    onClick={() => setOverviewViewMode('chart')}
                                />
                                <BsList
                                    className={overviewViewMode === 'list' ? 'active' : ''}
                                    onClick={() => setOverviewViewMode('list')}
                                />
                            </div>
                        </div>
                        <div className="gis-region-select-wrapper">
                            <select
                                className="gis-region-select"
                                value={selectedRegion || ''}
                                onChange={(e) => setSelectedRegion(e.target.value)}
                            >
                                {availableRegions.map(region => (
                                    <option key={region} value={region}>{region}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {loadingOverview ? (
                        overviewViewMode === 'chart' ? (
                            <div className="toa-charts">
                                {['Lightning Ignitions', 'HRL Classification'].map(title => (
                                    <div key={title} className="toa-chart">
                                        <h1 className="marketing-donut-title">{title}</h1>
                                        <div className="marketing-donut" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <div className="marketing-skeleton" style={{ width: 200, height: 200, borderRadius: '50%' }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="toa-charts">
                                {['Lightning Ignitions', 'HRL Classification'].map(title => (
                                    <div key={title} className="toa-chart">
                                        <h1 className="marketing-donut-title">{title}</h1>
                                        <div className="gis-progress-container">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <div key={i} className="gis-progress-row">
                                                    <div className="marketing-skeleton" style={{ width: 60, height: 14 }} />
                                                    <div className="gis-progress-bar">
                                                        <div className="marketing-skeleton" style={{ width: '100%', height: 10 }} />
                                                    </div>
                                                    <div className="marketing-skeleton" style={{ width: 50, height: 14 }} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : overviewViewMode === 'chart' ? (
                        <div className="toa-charts">
                            <div className="toa-chart">
                                <h1 className="marketing-donut-title">Lightning Ignitions</h1>
                                <div className="marketing-donut">
                                    <ReactApexChart
                                        options={
                                            lightningTotal === 0
                                                ? {
                                                    ...lightningChartOptions,
                                                    labels: ['No Data'],
                                                    colors: ['#E5E7EB'], // light grey
                                                    tooltip: { enabled: false },
                                                    states: {
                                                        hover: {
                                                            filter: {
                                                                type: 'none', // ✅ disable hover effect
                                                            },
                                                        },
                                                        active: {
                                                            filter: {
                                                                type: 'none',
                                                            },
                                                        },
                                                    },
                                                }
                                                : lightningChartOptions
                                        }
                                        series={lightningTotal === 0 ? [1] : lightningSeries} // ✅ pass single value for "No Data"
                                        type="donut"
                                        height="100%"
                                    />
                                </div>
                            </div>
                            <div className="toa-chart">
                                <h1 className="marketing-donut-title">HRL Classification</h1>
                                <div className="marketing-donut">
                                    <ReactApexChart
                                        options={
                                            hrlTotal === 0
                                                ? {
                                                    ...hrlChartOptions,
                                                    labels: ['No Data'],
                                                    colors: ['#E5E7EB'], // light grey
                                                    tooltip: { enabled: false },
                                                    states: {
                                                        hover: {
                                                            filter: {
                                                                type: 'none', // ✅ disable hover effect
                                                            },
                                                        },
                                                        active: {
                                                            filter: {
                                                                type: 'none',
                                                            },
                                                        },
                                                    },
                                                }
                                                : hrlChartOptions
                                        }
                                        series={hrlTotal === 0 ? [1] : hrlSeries} // ✅ pass single value for "No Data"
                                        type="donut"
                                        height="100%" />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="toa-charts">
                            <div className="toa-chart">
                                <h1 className="marketing-donut-title">Lightning Ignitions</h1>
                                <div className="gis-progress-container">
                                    {lightningLabels.map((label, i) => {
                                        const value = lightningSeries[i]
                                        const percent = lightningTotal ? ((value / lightningTotal) * 100).toFixed(1) : '0'
                                        return (
                                            <div key={label} className="gis-progress-row">
                                                <div className="gis-progress-label">{label}</div>
                                                <div className="gis-progress-bar">
                                                    <div className="gis-progress-fill" style={{ width: `${percent}%`, background: lightningColorMap[label as LightningStatus] }} />
                                                </div>
                                                <div className="gis-progress-value">{value} ({percent}%)</div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                            <div className="toa-chart">
                                <h1 className="marketing-donut-title">HRL Classification</h1>
                                <div className="gis-progress-container">
                                    {hrlLabels
                                        .map((label, i) => ({ label, value: hrlSeries[i] }))
                                        .filter(item => item.value > 0)
                                        .sort((a, b) => parseInt(a.label) - parseInt(b.label))
                                        .map(({ label, value }) => {
                                            const percent = hrlTotal ? ((value / hrlTotal) * 100).toFixed(1) : '0'
                                            return (
                                                <div key={label} className="gis-progress-row">
                                                    <div className="gis-progress-label">{getHrlLabel(label as HrlBucket)}</div>   {/* ← changed */}
                                                    <div className="gis-progress-bar">
                                                        <div className="gis-progress-fill" style={{ width: `${percent}%`, background: getHrlColor(label as HrlBucket) }} />
                                                    </div>
                                                    <div className="gis-progress-value">{value} ({percent}%)</div>
                                                </div>
                                            )
                                        })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>



            </div>
        </div>
    )
}

export default GisLightningAnalysis